import { useCallback, useMemo } from "react";
import { useSelector } from "../../../app/helpers";
import type { RootState } from "../../../app/rootReducer";
import { useRemoteFetchCompanyLocations } from "../../../hooks/Remote/Location/useRemoteFetchCompanyLocations";
import type { LocationUsage } from "../../Reports/typings/reports.types";
import {
  getDashboard2FirstBookableOfficeLocation,
  isUsableDashboard2OfficeLocation,
  resolveDashboard2OfficeLocationFallback,
  type Dashboard2OfficeLocationFallback
} from "../utils/dashboard2-favorite-locations.utils";

/**
 * Office site for a work-status quick book, plus a wait for the location catalog.
 */
export type Dashboard2OfficeLocationState = {
  /** Favorite site, or the first bookable site once the catalog has loaded. */
  location: LocationUsage | undefined;
  /** Returns that site now, waiting for the catalog when it is still loading. */
  resolveOfficeLocation: () => Promise<Dashboard2OfficeLocationFallback>;
};

/**
 * Office site for a work-status quick book.
 * Uses the last-month favorite when it is a real site; otherwise the first bookable location.
 */
export function useDashboard2OfficeLocation(
  favoriteLocation: LocationUsage | undefined
): Dashboard2OfficeLocationState {
  const preferredLocations = useSelector(
    (state: RootState) => state.login.settings?.preferredLocations
  );
  const usableFavorite = isUsableDashboard2OfficeLocation(favoriteLocation)
    ? favoriteLocation
    : undefined;

  const { data: companyLocations, refetch } = useRemoteFetchCompanyLocations({
    noMobileWorking: true,
    isFinanceRequest: false,
    isBookingSettings: true,
    enabled: usableFavorite == null
  });

  const location = useMemo(() => {
    if (usableFavorite) {
      return usableFavorite;
    }
    return getDashboard2FirstBookableOfficeLocation(companyLocations, preferredLocations);
  }, [companyLocations, preferredLocations, usableFavorite]);

  const resolveOfficeLocation = useCallback(async () => {
    if (usableFavorite) {
      return { kind: "ready" as const, location: usableFavorite };
    }
    if (companyLocations) {
      return resolveDashboard2OfficeLocationFallback(companyLocations, preferredLocations);
    }

    const result = await refetch({ cancelRefetch: false });
    return resolveDashboard2OfficeLocationFallback(result.data, preferredLocations);
  }, [companyLocations, preferredLocations, refetch, usableFavorite]);

  return { location, resolveOfficeLocation };
}
